import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Print as PrintIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Cancel as CancelIcon,
  Error as ErrorIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import InvoiceService from '../../services/invoice.service';
import { countryCodes, getDefaultCountryCode, formatPhoneNumber } from '../../utils/countryCodes';

const InvoiceDetail = ({ invoice, onEdit, onBack, onUpdate }) => {
  const [printDialogOpen, setPrintDialogOpen] = React.useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState('');
  const [canceling, setCanceling] = React.useState(false);
  const [cancelError, setCancelError] = React.useState('');
  const [emailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = React.useState(false);
  const [emailData, setEmailData] = React.useState({ emails: [], message: '' });
  const [whatsappData, setWhatsappData] = React.useState({ countryCode: '+502', phone: '', message: '' });
  const [sending, setSending] = React.useState({ email: false, whatsapp: false });
  const [sendError, setSendError] = React.useState('');

  if (!invoice) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography variant="h6">Invoice not found</Typography>
      </Box>
    );
  }

  const formatCurrency = (amount) => {
    const { formatCurrencySync } = require('../../utils/currencyUtils');
    return formatCurrencySync(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Handle both nested structure (old API) and flat structure (new API)
  const invoiceDetails = invoice.idetails || invoice.details || invoice.header?.idetails || [];
  const invoiceHeader = invoice.header || invoice;

  const calculateTotals = () => {
    const subtotal = invoiceDetails.reduce((sum, detail) => {
      return sum + (detail.cantidad * detail.precio);
    }, 0) || 0;

    const totalDiscount = invoiceDetails.reduce((sum, detail) => {
      const lineTotal = detail.cantidad * detail.precio;
      return sum + (lineTotal * (detail.descuento / 100));
    }, 0) || 0;

    const total = subtotal - totalDiscount;

    return { subtotal, totalDiscount, total };
  };

  const { subtotal, totalDiscount, total } = calculateTotals();

  const handlePrint = () => {
    setPrintDialogOpen(true);
    // In a real application, you would trigger the print dialog
    setTimeout(() => {
      window.print();
      setPrintDialogOpen(false);
    }, 100);
  };

  const handleViewPDF = () => {
    if (!invoice.fel_pdf) {
      alert('PDF not available. Please certify this invoice first.');
      return;
    }

    try {
      // Decode base64 PDF
      const byteCharacters = atob(invoice.fel_pdf);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create URL for the blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Open in new tab
      window.open(blobUrl, '_blank');
      
      // Clean up the URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error opening PDF:', error);
      alert('Error opening PDF. Please try again.');
    }
  };

  const handleCancelInvoice = async () => {
    if (!cancelReason.trim()) {
      setCancelError('Please provide a reason for cancellation');
      return;
    }

    try {
      setCanceling(true);
      setCancelError('');
      
      const response = await InvoiceService.cancelInvoice(invoice.id, cancelReason);
      
      if (response.data.success) {
        alert('Invoice cancelled successfully');
        setCancelDialogOpen(false);
        setCancelReason('');
        // Refresh the invoice data
        if (onUpdate) {
          onUpdate();
        }
      } else {
        setCancelError(response.data.message || 'Failed to cancel invoice');
      }
    } catch (error) {
      console.error('Error canceling invoice:', error);
      setCancelError(error.response?.data?.message || 'Failed to cancel invoice. Please try again.');
    } finally {
      setCanceling(false);
    }
  };

  const canCancelInvoice = () => {
    return (
      invoice.fel_serie &&
      invoice.fel_numero &&
      invoice.fel_status === 'certificado' &&
      !invoice.anulada
    );
  };

  const isCertified = () => {
    return invoice.fel_status === 'certificado' && invoice.fel_uuid;
  };

  const handleSendEmail = async () => {
    if (!emailData.emails.length || emailData.emails.some(email => !email.trim())) {
      setSendError('Please provide at least one valid email address');
      return;
    }

    try {
      setSending(prev => ({ ...prev, email: true }));
      setSendError('');
      
      const response = await InvoiceService.sendInvoiceEmail(invoice.id, {
        emails: emailData.emails.filter(email => email.trim()),
        message: emailData.message
      });
      
      if (response.data.success) {
        alert('Invoice sent via email successfully');
        setEmailDialogOpen(false);
        setEmailData({ emails: [], message: '' });
      } else {
        setSendError(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setSendError(error.response?.data?.message || 'Failed to send email. Please try again.');
    } finally {
      setSending(prev => ({ ...prev, email: false }));
    }
  };

  const handleSendWhatsApp = async () => {
    if (!whatsappData.phone.trim()) {
      setSendError('Please provide a phone number');
      return;
    }

    try {
      setSending(prev => ({ ...prev, whatsapp: true }));
      setSendError('');
      
      // Format phone number with country code
      const fullPhoneNumber = formatPhoneNumber(whatsappData.countryCode, whatsappData.phone);
      
      const response = await InvoiceService.sendInvoiceWhatsApp(invoice.id, {
        phone: fullPhoneNumber,
        message: whatsappData.message
      });
      
      if (response.data && response.data.success !== false) {
        alert('Invoice sent via WhatsApp successfully');
        setWhatsappDialogOpen(false);
        setWhatsappData({ countryCode: getDefaultCountryCode(), phone: '', message: '' });
        setSendError('');
      } else {
        setSendError(response.data?.message || 'Failed to send WhatsApp message');
      }
    } catch (error) {
      console.error('Error sending WhatsApp:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to send WhatsApp message. Please try again.';
      setSendError(errorMessage);
    } finally {
      setSending(prev => ({ ...prev, whatsapp: false }));
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
          <Typography variant="h4" component="h1" fontWeight="bold">
            Invoice Details
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Invoice #{invoice.id ? invoice.id.toString().padStart(4, '0') : 'N/A'} - {formatDate(invoice.date)}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Invoice Header */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <BusinessIcon sx={{ mr: 1 }} />
                      From
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="primary">
                      {invoice.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {invoice.address}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">{invoice.phone}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">{invoice.email}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <PersonIcon sx={{ mr: 1 }} />
                      Bill To
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {invoice.client?.name || 'Unknown Client'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {invoice.client?.email || 'No email provided'}
                    </Typography>
                    {invoice.client?.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {invoice.client.phone}
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Invoice Number</Typography>
                        <Typography variant="h6" fontWeight="bold">
                          INV-{invoice.id ? invoice.id.toString().padStart(4, '0') : 'N/A'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Invoice Date</Typography>
                        <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                          {formatDate(invoice.date)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">NIT/Tax ID</Typography>
                        <Typography variant="body1">{invoice.nit}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">Authorization</Typography>
                        <Typography variant="body1">{invoice.authorization}</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                {/* FEL Certification Info */}
                {invoice.fel_serie && invoice.fel_numero && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <ReceiptIcon sx={{ mr: 1 }} />
                      FEL Certification Information
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Serie</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {invoice.fel_serie}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Numero</Typography>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {invoice.fel_numero}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">UUID</Typography>
                          <Typography variant="body1">
                            {invoice.fel_uuid ? invoice.fel_uuid.substring(0, 20) + '...' : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Certification Date</Typography>
                          <Typography variant="body1">
                            {invoice.fel_fecha_certificacion ? formatDate(invoice.fel_fecha_certificacion) : 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">FEL Status</Typography>
                          <Chip
                            label={invoice.fel_status || 'Not Certified'}
                            color={invoice.fel_status === 'certificado' ? 'success' : 'default'}
                            size="small"
                            sx={{ mt: 0.5 }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {/* FEL Status and Error Display for non-certified invoices */}
                {!invoice.fel_serie && !invoice.fel_numero && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>FEL Status</Typography>
                      <Chip
                        label={invoice.fel_status || 'Not Certified'}
                        color={
                          invoice.fel_status === 'certificado' ? 'success' : 
                          invoice.fel_status === 'error' ? 'error' : 
                          'default'
                        }
                        size="small"
                        sx={{ mt: 0.5, mb: invoice.fel_error_message ? 2 : 0 }}
                      />
                      {invoice.fel_error_message && (
                        <Alert 
                          severity="error" 
                          icon={<ErrorIcon />}
                          sx={{ mt: 2, borderRadius: 2 }}
                        >
                          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                            <ErrorIcon sx={{ mr: 1 }} />
                            Certification Error
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            {invoice.fel_error_message}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Please review the invoice information and fix any issues before attempting to certify again.
                            {invoice.fel_intentos > 0 && (
                              <span> (Attempts: {invoice.fel_intentos})</span>
                            )}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                )}

                {invoice.visit && (
                  <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Related Visit</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {invoice.visit.title} - {formatDate(invoice.visit.schedule)}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Invoice Items */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                Invoice Items
              </Typography>
              
              <TableContainer component={Paper} sx={{ borderRadius: 2, mt: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'grey.50' }}>
                    <TableRow>
                      <TableCell><strong>Qty</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell align="right"><strong>Unit Price</strong></TableCell>
                      <TableCell align="right"><strong>Discount</strong></TableCell>
                      <TableCell align="right"><strong>Line Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceDetails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No invoice items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoiceDetails.map((detail, index) => (
                        <TableRow key={detail.id || index}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {detail.cantidad}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {detail.descripcion}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(detail.precio)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error">
                              {detail.descuento}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency((detail.cantidad * detail.precio) - ((detail.cantidad * detail.precio) * (detail.descuento / 100)))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Totals */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Paper sx={{ p: 3, minWidth: 350, backgroundColor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Invoice Summary</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>Subtotal:</Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {formatCurrency(subtotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography>Total Discount:</Typography>
                    <Typography variant="body1" fontWeight="medium" color="error">
                      -{formatCurrency(totalDiscount)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h5"><strong>Total Amount:</strong></Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      {formatCurrency(total)}
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Status and Actions */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={2}>
                    <Chip
                      label={invoice.anulada ? 'Cancelled' : 'Active'}
                      color={invoice.anulada ? 'error' : 'success'}
                      size="large"
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end" flexWrap="wrap">
                    {invoice.fel_pdf && (
                      <Button
                        variant="contained"
                        startIcon={<PdfIcon />}
                        onClick={handleViewPDF}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #e083eb 0%, #e5475c 100%)',
                          }
                        }}
                      >
                        View FEL PDF
                      </Button>
                    )}
                    {isCertified() && (
                      <>
                        <Button
                          variant="contained"
                          startIcon={<EmailIcon />}
                          onClick={() => {
                            const clientEmail = invoice.client?.email || '';
                            setEmailData({ 
                              emails: clientEmail ? [clientEmail] : [], 
                              message: '' 
                            });
                            setEmailDialogOpen(true);
                            setSendError('');
                          }}
                          sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            }
                          }}
                        >
                          Send Email
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<WhatsAppIcon />}
                          onClick={() => {
                            const clientPhone = invoice.client?.phone || '';
                            setWhatsappData({ 
                              phone: clientPhone, 
                              message: '' 
                            });
                            setWhatsappDialogOpen(true);
                            setSendError('');
                          }}
                          sx={{
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #20ba5a 0%, #0f7a6d 100%)',
                            }
                          }}
                        >
                          Send WhatsApp
                        </Button>
                      </>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={handlePrint}
                      sx={{ borderRadius: 2 }}
                    >
                      Print Invoice
                    </Button>
                    {canCancelInvoice() && (
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => setCancelDialogOpen(true)}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
                          }
                        }}
                      >
                        Cancel FEL Invoice
                      </Button>
                    )}
                    {!(invoice.fel_status === 'certificado' && invoice.fel_uuid) && (
                      <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={onEdit}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                          }
                        }}
                      >
                        Edit Invoice
                      </Button>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Print Dialog */}
      <Dialog open={printDialogOpen} onClose={() => setPrintDialogOpen(false)}>
        <DialogTitle>Print Invoice</DialogTitle>
        <DialogContent>
          <Typography>
            The invoice will be sent to your default printer. Make sure your printer is ready.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePrint} variant="contained">Print</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Invoice Dialog */}
      <Dialog 
        open={cancelDialogOpen} 
        onClose={() => !canceling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CancelIcon sx={{ mr: 1 }} />
            Cancel FEL Invoice
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {cancelError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {cancelError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            You are about to cancel a certified FEL invoice. This action will send a cancellation request to the SAT.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            <strong>Invoice:</strong> {invoice.fel_serie}-{invoice.fel_numero}
          </Typography>
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for Cancellation *"
            placeholder="Please provide a detailed reason for cancelling this invoice..."
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              setCancelError('');
            }}
            disabled={canceling}
            required
            helperText="This reason will be sent to SAT and cannot be changed later"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setCancelDialogOpen(false);
              setCancelReason('');
              setCancelError('');
            }}
            disabled={canceling}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCancelInvoice}
            variant="contained"
            color="error"
            disabled={canceling || !cancelReason.trim()}
            startIcon={canceling ? <CircularProgress size={20} color="inherit" /> : <CancelIcon />}
            sx={{
              background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #e53935 0%, #c62828 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {canceling ? 'Cancelling...' : 'Confirm Cancellation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Email Dialog */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => !sending.email && setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 1 }} />
            Send Invoice via Email
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            Send invoice INV-{invoice.id ? invoice.id.toString().padStart(4, '0') : 'N/A'} via email.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            label="Email Addresses *"
            placeholder="email1@example.com, email2@example.com"
            value={emailData.emails.join(', ')}
            onChange={(e) => {
              const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
              setEmailData({ ...emailData, emails });
              setSendError('');
            }}
            disabled={sending.email}
            required
            helperText="Enter email addresses separated by commas"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message (Optional)"
            placeholder="Add a custom message to include with the invoice..."
            value={emailData.message}
            onChange={(e) => {
              setEmailData({ ...emailData, message: e.target.value });
              setSendError('');
            }}
            disabled={sending.email}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setEmailDialogOpen(false);
              setEmailData({ emails: [], message: '' });
              setSendError('');
            }}
            disabled={sending.email}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={sending.email || !emailData.emails.length}
            startIcon={sending.email ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {sending.email ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send WhatsApp Dialog */}
      <Dialog 
        open={whatsappDialogOpen} 
        onClose={() => !sending.whatsapp && setWhatsappDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WhatsAppIcon sx={{ mr: 1 }} />
            Send Invoice via WhatsApp
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}
          <Typography variant="body1" gutterBottom>
            Send invoice INV-{invoice.id ? invoice.id.toString().padStart(4, '0') : 'N/A'} via WhatsApp.
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Country Code *</InputLabel>
              <Select
                value={whatsappData.countryCode}
                onChange={(e) => {
                  setWhatsappData({ ...whatsappData, countryCode: e.target.value });
                  setSendError('');
                }}
                label="Country Code *"
                disabled={sending.whatsapp}
              >
                {countryCodes.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.flag} {country.code} - {country.country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Phone Number *"
              placeholder="12345678"
              value={whatsappData.phone}
              onChange={(e) => {
                // Only allow numbers
                const value = e.target.value.replace(/\D/g, '');
                setWhatsappData({ ...whatsappData, phone: value });
                setSendError('');
              }}
              disabled={sending.whatsapp}
              required
              helperText="Enter phone number without country code"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Message (Optional)"
            placeholder="Add a custom message to include with the invoice..."
            value={whatsappData.message}
            onChange={(e) => {
              setWhatsappData({ ...whatsappData, message: e.target.value });
              setSendError('');
            }}
            disabled={sending.whatsapp}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setWhatsappDialogOpen(false);
              setWhatsappData({ countryCode: getDefaultCountryCode(), phone: '', message: '' });
              setSendError('');
            }}
            disabled={sending.whatsapp}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendWhatsApp}
            variant="contained"
            disabled={sending.whatsapp || !whatsappData.phone.trim()}
            startIcon={sending.whatsapp ? <CircularProgress size={20} color="inherit" /> : <WhatsAppIcon />}
            sx={{
              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #20ba5a 0%, #0f7a6d 100%)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)'
              }
            }}
          >
            {sending.whatsapp ? 'Sending...' : 'Send WhatsApp'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceDetail;
